import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Pill, Plus, Trash2, Send, X, Stethoscope, FlaskConical, CalendarDays, Loader2, CalendarPlus } from "lucide-react";

const FREQUENCY_PRESETS = ["1-0-0", "0-1-0", "0-0-1", "1-0-1", "1-1-1", "1-1-1-1", "SOS"];
const INSTRUCTION_PRESETS = [
  { v: "before_food", label: "Before food" },
  { v: "after_food", label: "After food" },
  { v: "empty_stomach", label: "Empty stomach" },
  { v: "at_bedtime", label: "At bedtime" },
  { v: "other", label: "Other (specify)" },
];

const emptyMed = () => ({
  name: "",
  dose: "",
  frequency: "1-0-1",
  duration_days: 5,
  instructions: "after_food",
  instructions_text: "",
});

const AUTO_FOLLOWUP_OPTIONS = [3, 5, 7, 10, 14, 21, 30];
// Snap any raw days value to the nearest option in AUTO_FOLLOWUP_OPTIONS.
function snapFollowupDays(raw) {
  if (!raw || raw <= 0) return 7;
  return AUTO_FOLLOWUP_OPTIONS.reduce((best, opt) =>
    Math.abs(opt - raw) < Math.abs(best - raw) ? opt : best
  );
}

/**
 * PrescriptionForm — structured prescription builder + WhatsApp/SMS dispatch.
 * Renders a Dialog. Trigger prop lets the caller inline the button as they wish.
 *
 * Props:
 *   api: authenticated axios instance
 *   patient: the patient object (id, name, mobile, ticket_number, prescription_v2?)
 *   trigger: optional custom trigger element; defaults to a green "Prescription" button
 *   onSaved: callback(updatedPrescription) after save
 */
export default function PrescriptionForm({ api, patient, trigger, onSaved }) {
  const [open, setOpen] = useState(false);
  const initial = patient?.prescription_v2 || {};
  const [medicines, setMedicines] = useState(initial.medicines?.length ? initial.medicines : [emptyMed()]);
  const [diagnosis, setDiagnosis] = useState(initial.diagnosis || "");
  const [testsInput, setTestsInput] = useState("");
  const [tests, setTests] = useState(initial.tests_advised || []);
  const [instructions, setInstructions] = useState(initial.special_instructions || "");
  const [nextVisit, setNextVisit] = useState(initial.next_visit_date || "");
  const [autoFollowupEnabled, setAutoFollowupEnabled] = useState(true);
  const [autoFollowupDays, setAutoFollowupDays] = useState("7");
  const [busy, setBusy] = useState(false);

  // Auto-suggest a follow-up window from the medicine with the longest duration.
  // If the doctor hasn't manually touched autoFollowupDays yet we keep it synced.
  const maxDuration = medicines.reduce((mx, m) => {
    const d = parseInt(m.duration_days) || 0;
    return d > mx ? d : mx;
  }, 0);
  const suggested = maxDuration > 0 ? String(snapFollowupDays(maxDuration)) : "7";
  const [userTouchedDays, setUserTouchedDays] = useState(false);
  if (!userTouchedDays && autoFollowupDays !== suggested) {
    // Sync without an effect — cheap enough during render, only mutates local state.
    setAutoFollowupDays(suggested);
  }

  const updateMed = (idx, patch) => setMedicines((r) => r.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const removeMed = (idx) => setMedicines((r) => (r.length === 1 ? r : r.filter((_, i) => i !== idx)));
  const addMed = () => setMedicines((r) => [...r, emptyMed()]);

  const addTestFromInput = () => {
    const t = testsInput.trim();
    if (!t) return;
    if (tests.includes(t)) { setTestsInput(""); return; }
    setTests((r) => [...r, t]);
    setTestsInput("");
  };
  const removeTest = (t) => setTests((r) => r.filter((x) => x !== t));

  const submit = async () => {
    const cleanMeds = medicines
      .filter((m) => (m.name || "").trim())
      .map((m) => ({
        name: m.name.trim(),
        dose: m.dose?.trim() || null,
        frequency: m.frequency?.trim() || null,
        duration_days: m.duration_days ? parseInt(m.duration_days) : null,
        instructions: m.instructions || null,
        instructions_text: (m.instructions === "other" ? (m.instructions_text || "").trim() : "") || null,
      }));
    if (!cleanMeds.length && !diagnosis.trim() && tests.length === 0) {
      toast.error("Add at least one medicine, diagnosis or advised test.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/prescription-structured`, {
        medicines: cleanMeds,
        diagnosis: diagnosis.trim() || null,
        tests_advised: tests,
        special_instructions: instructions.trim() || null,
        next_visit_date: nextVisit || null,
        auto_followup_days: autoFollowupEnabled ? parseInt(autoFollowupDays) : null,
        auto_followup_note: autoFollowupEnabled ? "Follow-up after prescribed course" : null,
        auto_followup_lang: "en",
      });
      const waHint = data.wa_status === "sent" ? "WhatsApp sent." : "WhatsApp queued (gateway not configured).";
      const fuHint = data.auto_followup ? ` Follow-up set for ${data.auto_followup.followup_date}.` : "";
      toast.success(`Prescription saved. ${waHint}${fuHint}`);
      setOpen(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 h-9" data-testid={`open-prescription-${patient?.id}`}>
            <Pill className="w-4 h-4" /> Prescription
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="prescription-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Pill className="w-5 h-5" /> Structured Prescription — {patient?.name}
          </DialogTitle>
          <DialogDescription>
            Save a structured prescription. On submit we WhatsApp the patient (falls back to SMS) and store the record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {/* Medicines table */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Pill className="w-4 h-4" /> Medicines</div>
              <Button variant="outline" size="sm" onClick={addMed} className="gap-1 h-8" data-testid="prescription-add-med">
                <Plus className="w-3.5 h-3.5" /> Add medicine
              </Button>
            </div>
            <div className="space-y-2">
              {medicines.map((m, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 p-2 rounded-lg border border-slate-200" data-testid={`prescription-med-row-${idx}`}>
                  <div className="col-span-4">
                    <Label className="text-[10px] text-slate-500">Medicine</Label>
                    <Input value={m.name} onChange={(e) => updateMed(idx, { name: e.target.value })} placeholder="Paracetamol 500 mg" data-testid={`med-name-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-slate-500">Dose</Label>
                    <Input value={m.dose} onChange={(e) => updateMed(idx, { dose: e.target.value })} placeholder="500 mg" data-testid={`med-dose-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-slate-500">Frequency</Label>
                    <Input
                      list={`freq-list-${idx}`}
                      value={m.frequency}
                      onChange={(e) => updateMed(idx, { frequency: e.target.value })}
                      placeholder="1-0-1"
                      data-testid={`med-freq-${idx}`}
                    />
                    <datalist id={`freq-list-${idx}`}>
                      {FREQUENCY_PRESETS.map((f) => <option key={f} value={f} />)}
                    </datalist>
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px] text-slate-500">Days</Label>
                    <Input type="number" min="0" max="365" value={m.duration_days || ""} onChange={(e) => updateMed(idx, { duration_days: e.target.value })} data-testid={`med-days-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-slate-500">Instructions</Label>
                    <Select value={m.instructions || ""} onValueChange={(v) => updateMed(idx, { instructions: v })}>
                      <SelectTrigger className="h-9" data-testid={`med-instr-${idx}`}><SelectValue placeholder="--" /></SelectTrigger>
                      <SelectContent>
                        {INSTRUCTION_PRESETS.map((i) => <SelectItem key={i.v} value={i.v}>{i.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex items-end justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMed(idx)}
                      disabled={medicines.length === 1}
                      className="h-9"
                      data-testid={`med-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4 text-slate-500" />
                    </Button>
                  </div>
                  {m.instructions === "other" && (
                    <div className="col-span-12">
                      <Input
                        value={m.instructions_text || ""}
                        onChange={(e) => updateMed(idx, { instructions_text: e.target.value })}
                        placeholder="Custom instruction, e.g. Take with warm water"
                        data-testid={`med-instr-text-${idx}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Diagnosis */}
          <section>
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Stethoscope className="w-4 h-4" /> Diagnosis</Label>
            <Textarea
              rows={2}
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Provisional diagnosis or clinical impression"
              data-testid="prescription-diagnosis"
            />
          </section>

          {/* Tests advised */}
          <section>
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1"><FlaskConical className="w-4 h-4" /> Tests advised</Label>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-2 min-h-11">
              {tests.map((t) => (
                <Badge key={t} variant="secondary" className="gap-1" data-testid={`prescription-test-chip-${t}`}>
                  {t}
                  <button type="button" onClick={() => removeTest(t)} className="hover:text-rose-600" aria-label={`remove ${t}`}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              <Input
                className="flex-1 min-w-[120px] border-0 focus-visible:ring-0 shadow-none h-8 px-1"
                placeholder="Type a test then press Enter or ,"
                value={testsInput}
                onChange={(e) => setTestsInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTestFromInput(); }
                  else if (e.key === "Backspace" && !testsInput && tests.length) {
                    setTests((r) => r.slice(0, -1));
                  }
                }}
                onBlur={addTestFromInput}
                data-testid="prescription-tests-input"
              />
            </div>
          </section>

          {/* Special instructions & next visit */}
          <section className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Special instructions</Label>
              <Textarea
                rows={3}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Rest for 3 days; drink plenty of fluids"
                data-testid="prescription-special-instructions"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1"><CalendarDays className="w-4 h-4" /> Next visit (optional)</Label>
              <Input type="date" value={nextVisit} onChange={(e) => setNextVisit(e.target.value)} data-testid="prescription-next-visit" />
            </div>
          </section>

          {/* Auto follow-up suggestion */}
          <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="prescription-auto-followup">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <CalendarPlus className="w-4 h-4 text-emerald-600 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-slate-800">Also schedule a follow-up</div>
                  <div className="text-xs text-slate-500">
                    Auto-suggested from the longest medicine course
                    {maxDuration > 0 && <> ({maxDuration} days)</>}.
                    UpchaarQ will WhatsApp/SMS the patient one day before.
                  </div>
                </div>
              </div>
              <Switch
                checked={autoFollowupEnabled}
                onCheckedChange={setAutoFollowupEnabled}
                data-testid="prescription-auto-followup-toggle"
              />
            </div>
            {autoFollowupEnabled && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="prescription-auto-followup-days-wrap">
                {AUTO_FOLLOWUP_OPTIONS.map((d) => {
                  const active = String(d) === autoFollowupDays;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => { setUserTouchedDays(true); setAutoFollowupDays(String(d)); }}
                      className={`px-3 py-1 rounded-full border text-xs font-semibold transition ${
                        active
                          ? "bg-emerald-500 text-white border-emerald-500"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                      }`}
                      data-testid={`prescription-auto-followup-opt-${d}`}
                    >
                      {d} days
                    </button>
                  );
                })}
                {maxDuration > 0 && String(snapFollowupDays(maxDuration)) === autoFollowupDays && !userTouchedDays && (
                  <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">Suggested</Badge>
                )}
              </div>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
            data-testid="prescription-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Sending…" : "Generate & Send Prescription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
